import React, { ReactElement, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Link from '@/shared/ui/Link';
import Button from '@/features/marketing/components/Button';
import menuDefaults from '@/shared/data/MenuDefaults';
import { HeaderContext } from '@/shared/contexts/Header';
import ShipmentTrackingForm from '@/features/marketing/components/ShipmentTrackingForm';
import Accordion from '@/features/marketing/components/Accordion';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';

const iconMap : { [key : string] : any } = {
    HelpOutlineRounded: HelpOutlineRoundedIcon,
    InfoOutlined: InfoOutlinedIcon,
    LocalShippingOutlined: LocalShippingOutlinedIcon,
    AddCircleOutlineRounded: AddCircleOutlineRoundedIcon,
    ComputerRounded: ComputerRoundedIcon,
    ExploreOutlined: ExploreOutlinedIcon,
    StarsOutlined: StarsOutlinedIcon,
};

type Props = {};

const HeaderMobileMenu : React.FC<Props> = () : ReactElement | null => {
    const { showMobileMenu } = useContext(HeaderContext);

    return (
        <AnimatePresence>
            {
                showMobileMenu && (
                    <motion.div
                        initial={{ transform: 'translateX(100%)' }}
                        animate={{ transform: 'translateX(0)', transition: { duration: 0.3 } }}
                        exit={{ transform: 'translateX(100%)', transition: { duration: 0.2 } }}
                        className='h-[calc(100vh-5rem)] fixed top-[3rem] lg:top-[5rem] w-full z-[998] backdrop-blur-sm'
                    >
                        <motion.div
                            initial={false}
                            animate={showMobileMenu ? 'open' : 'closed'}
                            variants={{
                                open: {
                                    transition: {
                                        staggerChildren: 0.1,
                                        staggerDirection: 1,
                                    },
                                },
                            }}
                            className='bg-white shadow-lg max-h-[80vh] overflow-y-scroll'
                        >
                            <div className='flex lg:hidden flex-col bg-[#F5F7FC] py-[30px] px-[36px]'>
                                <div className='flex flex-col gap-[30px]'>
                                    {
                                        menuDefaults.map((menuSection) => {
                                            return (
                                                <motion.div
                                                    key={menuSection.id}
                                                    variants={{
                                                        closed: { opacity: 0 },
                                                        open: { opacity: 1 },
                                                    }}
                                                >
                                                    {
                                                        menuSection.items.length === 0 && <div>
                                                            <Link
                                                                to={menuSection.path}
                                                                className='text-[24px] text-[#14274D] leading-[36px] font-bold duration-200 hover:text-[#633998]'
                                                            >
                                                                {menuSection.name.translation.fallback}
                                                            </Link>
                                                        </div>
                                                    }
                                                    {
                                                        menuSection.items.length > 0 && <Accordion
                                                            className='py-0 px-0'
                                                            heading={(
                                                                <Link
                                                                    to={menuSection.path}
                                                                    className='text-[24px] text-[#14274D] leading-[36px] font-bold duration-200 hover:text-[#633998]'
                                                                >
                                                                    {menuSection.name.translation.fallback}
                                                                </Link>
                                                            )}
                                                            content={(
                                                                <div className='flex flex-col'>

                                                                    {menuSection.items.map((item, index) => {
                                                                        return (
                                                                            <div key={index} className='flex flex-col gap-[15px] ml-3'>
                                                                                {item.title && (
                                                                                    <h5
                                                                                        className={`text-[#14274D] text-[18px] font-semibold flex items-center gap-2 ${index !== 0 ? 'mt-8' : 'mt-0'}`}
                                                                                    >
                                                                                        {item.icon && iconMap[item.icon] && React.createElement(iconMap[item.icon], { sx: { fontSize: 20, color: '#14274D' } })}
                                                                                        {item.title?.translation.fallback}
                                                                                    </h5>
                                                                                )}

                                                                                {
                                                                                    item.links.map((link, index) => {
                                                                                        return (
                                                                                            <Link
                                                                                                key={index}
                                                                                                to={link.path}
                                                                                                className='text-[#14274D] font-normal'
                                                                                            >
                                                                                                &#x2022; {link.label.translation.fallback}
                                                                                            </Link>
                                                                                        );
                                                                                    })
                                                                                }
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        />
                                                    }
                                                </motion.div>
                                            );
                                        })
                                    }
                                </div>

                                <div className='my-[30px] border-t-2 border-[#5F7194] opacity-30' />

                                <motion.div
                                    variants={{
                                        closed: { opacity: 0 },
                                        open: { opacity: 1 },
                                    }}
                                    className='flex flex-col gap-[20px]'
                                >
                                    <Link to='/registrace' >
                                        <Button className='w-full text-[18px] leading-[24px]'>Vytvořit účet zdarma</Button>
                                    </Link>

                                    <Link to='/prihlaseni'>
                                        <Button type='empty' className='w-full text-[18px] leading-[24px]'>Přihlásit</Button>
                                    </Link>
                                </motion.div>
                            </div>

                            <motion.div
                                variants={{
                                    closed: { opacity: 0 },
                                    open: { opacity: 1 },
                                }}
                                className='py-[30px] px-[36px] flex lg:hidden'
                            >
                                <ShipmentTrackingForm />
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )
            }
        </AnimatePresence>
    );
};

export default HeaderMobileMenu;
